import type { AlertLeader } from './alert-leader';

/* ==========================================================================
   "Silenciar por 30 min" (change customer-agent-handoff, D19)

   Cala o som e a notificação do atendimento neste navegador por 30 minutos;
   a faixa, o título e o contador continuam. O prazo fica no `localStorage`
   (`vaptmenu.conversation-alerts.muted-until`), para valer também na aba que
   abrir depois, e as abas abertas sabem pelo canal da aba líder. Leitura e
   escrita em `try/catch`: com o armazenamento bloqueado, o silêncio vale só
   para a aba, sem erro na tela. O dono manteve o botão em 02/10/2026 (D19 e
   "Escolhas de desenho" do design).
   ========================================================================== */

export const ALERT_MUTE_STORAGE_KEY = 'vaptmenu.conversation-alerts.muted-until';
export const ALERT_MUTE_MS = 30 * 60_000;

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): StorageLike | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** O fim do silêncio gravado, ou nulo (sem silêncio, valor inválido ou armazenamento bloqueado). */
export function readMutedUntil(storage: StorageLike | null = defaultStorage()): number | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(ALERT_MUTE_STORAGE_KEY);
    if (!raw) return null;
    const until = Number(raw);
    return Number.isFinite(until) && until > 0 ? until : null;
  } catch {
    return null;
  }
}

/** Grava o fim do silêncio; falso quando o armazenamento recusou. */
export function writeMutedUntil(until: number, storage: StorageLike | null = defaultStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(ALERT_MUTE_STORAGE_KEY, String(until));
    return true;
  } catch {
    return false;
  }
}

export function isMuted(mutedUntil: number | null, now: number): boolean {
  return mutedUntil !== null && mutedUntil > now;
}

export interface AlertMuteOptions {
  leader: Pick<AlertLeader, 'onMute' | 'postMute'> | null;
  storage?: StorageLike | null;
  now?: () => number;
}

export interface AlertMute {
  /** O fim do silêncio que vale nesta aba (o do armazenamento, o do canal ou o desta aba). */
  mutedUntil(): number | null;
  isMuted(): boolean;
  /** Silencia por 30 min neste navegador e avisa as outras abas; devolve o fim do silêncio. */
  mute(): number;
  subscribe(listener: (mutedUntil: number | null) => void): () => void;
  stop(): void;
}

export function createAlertMute(options: AlertMuteOptions): AlertMute {
  const now = options.now ?? (() => Date.now());
  const storage = options.storage === undefined ? defaultStorage() : options.storage;
  let local: number | null = readMutedUntil(storage);
  const listeners = new Set<(mutedUntil: number | null) => void>();

  const set = (until: number) => {
    if (local !== null && local >= until) return;
    local = until;
    for (const listener of listeners) listener(local);
  };

  const unsubscribeChannel = options.leader?.onMute((until) => set(until)) ?? (() => undefined);

  const current = () => {
    // Outra aba sem canal pode ter gravado depois: o armazenamento também conta.
    const stored = readMutedUntil(storage);
    if (stored !== null && (local === null || stored > local)) local = stored;
    return local;
  };

  return {
    mutedUntil: current,
    isMuted: () => isMuted(current(), now()),
    mute: () => {
      const until = now() + ALERT_MUTE_MS;
      writeMutedUntil(until, storage);
      set(until);
      options.leader?.postMute(until);
      return until;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    stop: () => {
      unsubscribeChannel();
      listeners.clear();
    },
  };
}
